import { Component, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatchSelection } from '../../services/stage-state.service';
import { ProductType, RateDisplay } from './broker-profile.models';
import { BrokerProfileRateHistoryComponent } from './broker-profile-rate-history.component';

/**
 * Client-facing mode body for the broker profile flyout.
 *
 * Single-slot variant for the rep + client in the same room:
 * marketing-grade chrome (carrier logo hero, big rate callout, 6 decision
 * tiles, rate-history chart stub) with "Run in Calculator" footer CTA.
 *
 * Mockup: docs/competitive/mockups/broker_profile_v1/02-brochure-flyout.html
 *
 * D1 decision: hide body disclaimers, keep a single 10px footer line
 * "* For financial professional use only · not a solicitation".
 * D2 decision: Run-in-Calculator mints a new case (fresh caseId).
 */
@Component({
  selector: 'app-broker-profile-clientfacing-body',
  standalone: true,
  imports: [CommonModule, BrokerProfileRateHistoryComponent],
  template: `
    <!-- Carrier logo hero (96px, centered) ─────────────────── -->
    <div class="logo-hero">
      <div class="logo-hero-mark">
        <span class="logo-hero-text">{{ shortCarrier() }}</span>
        <span class="logo-hero-sub">Annuity &amp; Life</span>
      </div>
      <div class="carrier-line">
        {{ product().companyName }}
        @if (product().bestid) {
          <span class="ambest-inline">{{ product().bestid }} AM BEST</span>
        }
      </div>
      <h2 class="prod-name">{{ product().productName }}</h2>
      <div class="prod-sub">{{ product().riderName }}</div>
    </div>

    <!-- Rate callout (big, hero-size) ──────────────────────── -->
    <div class="rate-card">
      <div class="rate-bar"></div>
      <div>
        <div class="rate-inner">
          <div class="rate-kicker">{{ rateKicker() }}</div>
          @if (isUncapped()) {
            <div class="rate-big rate-big--word">Uncapped</div>
          } @else {
            <div class="rate-big">{{ rateValueText() }}<span class="pct">%</span></div>
          }
          <div class="rate-meta">
            @if (rateMeta(); as meta) {
              <span class="flat">{{ meta }}</span>
            } @else {
              <span class="flat">{{ product().rollupPer ? product().rollupPer + '% rollup' : 'Fixed factor' }}</span>
              @if (product().maxRollupYears) {
                <span>for {{ product().maxRollupYears }}yr</span>
              }
            }
          </div>
        </div>
        <div class="rate-foot">
          <b>{{ product().benifitBaseType || 'Level base' }}</b>
          <span>· surrender {{ product().surryr }}yr</span>
        </div>
      </div>
    </div>

    <!-- Feature tiles 2×3 ──────────────────────────────────── -->
    <div class="tiles">
      <div class="tile pos">
        <div class="tile-lbl">Surrender period</div>
        <div class="tile-val">{{ product().surryr }}yr</div>
        <div class="tile-sub">Walkaway penalty period</div>
      </div>
      <div class="tile neu">
        <div class="tile-lbl">Free withdrawal</div>
        <div class="tile-val">10%</div>
        <div class="tile-sub">Annual · after yr 1</div>
      </div>
      <div class="tile neu">
        <div class="tile-lbl">MVA</div>
        <div class="tile-val word">{{ mvaDisplay() }}</div>
        <div class="tile-sub">Market value adjustment</div>
      </div>
      <div class="tile pos">
        <div class="tile-lbl">Death benefit</div>
        <div class="tile-val word">Account value</div>
        <div class="tile-sub">Paid in full to beneficiary</div>
      </div>
      <div class="tile neu">
        <div class="tile-lbl">Issue ages</div>
        <div class="tile-val">40–80</div>
        <div class="tile-sub">Owner eligibility range</div>
      </div>
      <div class="tile pos">
        <div class="tile-lbl">Carrier strength</div>
        <div class="tile-val">{{ product().bestid || '—' }}</div>
        <div class="tile-sub">AM Best rating</div>
      </div>
    </div>

    <!-- Rate history sparkline ───────────────────────────── -->
    <app-broker-profile-rate-history
      [productType]="productType()"
      [productid]="product().productid"
      variant="narrow" />

    <!-- Footer action: Run in Calculator CTA ───────────────── -->
    <div class="cf-actions">
      <button class="cf-cta" (click)="runInCalculator.emit()">
        Run in Calculator
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="5" y1="12" x2="19" y2="12"/>
          <polyline points="12 5 19 12 12 19"/>
        </svg>
      </button>
      <div class="cf-disclaimer">* For financial professional use only · not a solicitation</div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      font-family: var(--sans);
      color: var(--text);
    }

    /* Logo hero band */
    .logo-hero {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 28px 20px 18px;
      background: var(--panel);
      border: 1px solid var(--border);
      border-radius: 4px;
      margin-bottom: 14px;
    }
    .logo-hero-mark {
      width: 160px; height: 96px;
      background: var(--panel2);
      border: 1px solid var(--border-light);
      border-radius: 3px;
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      margin-bottom: 14px;
    }
    .logo-hero-text {
      font-family: var(--sans);
      font-weight: 800;
      font-size: 22px;
      letter-spacing: -0.01em;
      color: var(--blue-2);
      line-height: 1;
    }
    .logo-hero-sub {
      font-family: var(--mono);
      font-size: 9px; font-weight: 700;
      color: var(--muted);
      letter-spacing: 0.12em;
      text-transform: uppercase;
      margin-top: 6px;
    }
    .carrier-line {
      font-family: var(--mono);
      font-size: 11px; font-weight: 700;
      color: var(--muted);
      letter-spacing: 0.1em;
      text-transform: uppercase;
      display: flex; align-items: center; gap: 10px;
      margin-bottom: 6px;
    }
    .ambest-inline {
      font-family: var(--mono);
      font-size: 9px; font-weight: 700;
      background: var(--blue-10);
      color: var(--blue);
      padding: 2px 7px;
      border-radius: 3px;
      letter-spacing: 0.04em;
    }
    .prod-name {
      font-size: 22px; font-weight: 700;
      color: var(--navy);
      margin: 0;
      letter-spacing: -0.015em;
      line-height: 1.15;
      text-align: center;
    }
    .prod-sub {
      font-size: 12px;
      color: var(--muted);
      margin-top: 4px;
      text-align: center;
    }

    /* Rate callout (hero-sized) */
    .rate-card {
      display: grid;
      grid-template-columns: 5px 1fr;
      background: var(--panel2);
      border: 1px solid var(--border);
      border-radius: 4px;
      overflow: hidden;
      margin-bottom: 14px;
    }
    .rate-bar { background: var(--pos); }
    .rate-inner {
      padding: 16px 20px 12px;
    }
    .rate-kicker {
      font-family: var(--mono);
      font-size: 10px; font-weight: 700;
      color: var(--muted);
      letter-spacing: 0.14em;
      text-transform: uppercase;
    }
    .rate-big {
      font-family: var(--mono);
      font-size: 52px; font-weight: 700;
      color: var(--navy);
      letter-spacing: -0.03em;
      line-height: 0.95;
      margin-top: 4px;
    }
    .rate-big .pct {
      font-size: 26px;
      color: var(--muted);
      margin-left: 4px;
      font-weight: 500;
    }
    .rate-big--word {
      font-family: var(--sans);
      font-size: 38px;
      color: var(--pos);
      letter-spacing: -0.02em;
    }
    .rate-meta {
      display: flex;
      gap: 12px;
      margin-top: 8px;
      font-family: var(--mono);
      font-size: 11px;
      color: var(--muted);
      letter-spacing: 0.02em;
    }
    .rate-meta .flat {
      color: var(--muted);
      font-weight: 700;
    }
    .rate-foot {
      padding: 8px 20px;
      border-top: 1px solid var(--border-light);
      display: flex; gap: 10px;
      font-size: 11px;
      color: var(--text);
      background: var(--panel);
      font-family: var(--mono);
      letter-spacing: 0.02em;
    }
    .rate-foot b {
      color: var(--navy);
      font-weight: 700;
      font-family: var(--sans);
      font-size: 12px;
    }

    /* Feature tiles 2×3 */
    .tiles {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 10px;
      margin-bottom: 14px;
    }
    .tile {
      background: var(--panel);
      border: 1px solid var(--border);
      border-radius: 3px;
      padding: 12px 14px;
      min-height: 88px;
    }
    .tile.pos { border-left: 3px solid var(--pos); }
    .tile.neu { border-left: 3px solid var(--blue-2); }
    .tile-lbl {
      font-family: var(--mono);
      font-size: 9px; font-weight: 700;
      color: var(--muted);
      letter-spacing: 0.12em;
      text-transform: uppercase;
    }
    .tile-val {
      font-family: var(--mono);
      font-size: 17px; font-weight: 700;
      color: var(--navy);
      margin-top: 4px;
      letter-spacing: -0.01em;
    }
    .tile-val.word {
      font-family: var(--sans);
      font-size: 13px;
      letter-spacing: 0;
    }
    .tile-sub {
      font-size: 10px;
      color: var(--muted);
      margin-top: 4px;
      line-height: 1.3;
    }

    /* Rate history — stub block for Phase 2B-1 */
    .hist {
      background: var(--panel);
      border: 1px solid var(--border);
      border-radius: 4px;
      padding: 14px 16px;
      margin-bottom: 14px;
    }
    .hist-hd {
      display: flex;
      align-items: baseline;
      margin-bottom: 10px;
    }
    .hist-title {
      font-family: var(--mono);
      font-size: 10px; font-weight: 700;
      color: var(--muted);
      letter-spacing: 0.12em;
      text-transform: uppercase;
    }
    .hist-range {
      margin-left: auto;
      font-family: var(--mono);
      font-size: 9px;
      color: var(--label);
      letter-spacing: 0.04em;
    }
    .chart-stub {
      height: 110px;
      background: var(--panel2);
      border: 1px dashed var(--border);
      border-radius: 3px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .chart-stub-label {
      font-size: 11px;
      color: var(--muted);
      font-style: italic;
    }

    /* Footer CTA + disclaimer line */
    .cf-actions {
      margin-top: 4px;
    }
    .cf-cta {
      width: 100%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      background: var(--navy);
      color: #fff;
      font-family: var(--sans);
      font-size: 14px; font-weight: 700;
      padding: 12px 16px;
      border: 1px solid var(--navy);
      border-radius: 3px;
      cursor: pointer;
      letter-spacing: 0.01em;
    }
    .cf-cta:hover { background: var(--navy2); }
    .cf-cta svg { width: 14px; height: 14px; }
    .cf-disclaimer {
      font-size: 10px;
      color: var(--muted);
      text-align: center;
      margin-top: 10px;
      font-style: italic;
      letter-spacing: 0.02em;
    }
  `],
})
export class BrokerProfileClientFacingBodyComponent {
  readonly product = input.required<MatchSelection>();
  readonly productType = input<ProductType>('ia');
  readonly rateDisplay = input<RateDisplay | undefined>(undefined);
  readonly runInCalculator = output();

  readonly shortCarrier = computed(() => {
    return this.product().companyName.split(' ')[0]?.toUpperCase() ?? 'CARRIER';
  });

  readonly factor = computed(() => {
    const p = this.product();
    return p.factorJoint || p.factorSingle || 0;
  });

  /** Rate shown in the big callout. Prefers the rate-sheet override when
   *  launched from a rate sheet; falls back to the GMWB factor for Case
   *  mode scenarios. */
  /** True when the rate-sheet passed a 0 value with an "Uncapped" kicker. */
  readonly isUncapped = computed(() => {
    const o = this.rateDisplay();
    return !!(o && o.value === 0 && /uncapped/i.test(o.kicker));
  });

  readonly rateValueText = computed(() => {
    const override = this.rateDisplay();
    if (override) return override.value.toFixed(2);
    return (this.factor() * 100).toFixed(2);
  });

  readonly rateKicker = computed(() => {
    const override = this.rateDisplay();
    if (override) return override.kicker;
    const p = this.product();
    return p.rollupPer ? `Income factor @ ${p.rollupPer}% rollup` : 'Current income factor';
  });

  readonly rateMeta = computed(() => this.rateDisplay()?.meta);

  readonly mvaDisplay = computed(() => {
    // MVA isn't in MatchSelection — placeholder for P2B-2 when we add metadata
    return '—';
  });
}
